import type React from "react"
import { useState } from "react"
import { MessageSquare, Send, Users } from "lucide-react"
import { Button } from "../ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Avatar, AvatarFallback } from "../ui/avatar"
import { Textarea } from "../ui/textarea"
import { formatTimeAgo as formatTimeAgoUtil } from "../../utils/format-time"

interface Comment {
  id: string
  comment: string
  commented_by: string
  commented_by_display: string
  commented_on: string
  lead: string
}

interface NotesCardProps {
  comments: Comment[]
  onAddComment: (comment: string) => void
  maxHeight?: string
  maxCharacters?: number
}

// Helper function to format time ago
const formatTimeAgo = (dateString: string): string => {
  return formatTimeAgoUtil(dateString)
}

export default function NotesCard({ 
  comments, 
  onAddComment, 
  maxHeight = "max-h-96",
  maxCharacters = 255
}: NotesCardProps) {
  const [inputValue, setInputValue] = useState("")

  const remainingCharacters = maxCharacters - inputValue.length
  const isAtLimit = remainingCharacters < 0
  const canSend = inputValue.trim().length > 0 && !isAtLimit

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (canSend) {
        sendComment()
      }
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    
    // Allow input up to exactly the character limit (including 0 remaining)
    if (newValue.length <= maxCharacters) {
      setInputValue(newValue)
    }
  }

  const sendComment = () => {
    if (!canSend) return

    onAddComment(inputValue)
    setInputValue("")
  }

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="border-b border-slate-200">
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Notes
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        {/* Comments List */}
        <div className={`space-y-4 mb-6 ${maxHeight} overflow-y-auto`}>
          {comments.length > 0 ? (
            comments.map((comment) => (
              <div key={comment.id} className="flex space-x-3">
                <Avatar className="h-8 w-8 flex-shrink-0">
                  <AvatarFallback className="bg-slate-200 text-white text-xs uppercase">
                    {comment.commented_by_display
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <p className="text-sm text-slate-900 break-words overflow-wrap-anywhere">{comment.comment}</p>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-slate-500">
                      {comment.commented_by_display} • {formatTimeAgo(comment.commented_on)}
                    </p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Users className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No notes yet</p>
            </div>
          )}
        </div>

        {/* Add Note Form */}
        <div className="space-y-3">
          <Textarea
            placeholder="Add a note..."
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            className={`min-h-[80px] max-h-[200px] border-slate-300 focus:border-slate-500 ${
              isAtLimit ? 'border-red-300 focus:border-red-500' : ''
            }`}
          />
          
          {/* Character Counter */}
          <div className="flex items-center justify-between">
            <p className={`text-xs ${
              remainingCharacters < 50 ? 'text-red-500' : 'text-slate-500'
            }`}>
              {remainingCharacters} characters remaining
            </p>
            
            {/* Action Buttons */}
            <div className="flex space-x-2">
              <Button 
                size="sm" 
                onClick={sendComment} 
                disabled={!canSend}
                className={!canSend ? 'opacity-50 cursor-not-allowed' : ''}
              >
                <Send className="h-4 w-4 mr-2" />
                Send
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
} 